import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Paper,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Chip } from '@mui/material';
import { Customer } from '../types';
import { api } from '../api';
import { authStorage } from '../auth';

interface CustomerListPageProps {
  onEditCustomer: (customer: Customer) => void;
  onCreatePurchase: (customer: Customer) => void;
  setReloadCallback: (callback: (() => void) | null) => void;
}

export const CustomerListPage: React.FC<CustomerListPageProps> = ({ onEditCustomer, onCreatePurchase, setReloadCallback }) => {
  const [customers, setCustomers] = React.useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = React.useState<Customer[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [sortField, setSortField] = React.useState<'name' | 'phone'>('name');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedCustomerDelete, setSelectedCustomerDelete] = React.useState<Customer | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  React.useEffect(() => {
    setIsAdmin(authStorage.isAdmin());
  }, []);

  React.useEffect(() => {
    loadCustomers();
  }, []);

  React.useEffect(() => {
    // Đăng ký callback để reload danh sách customer
    setReloadCallback(() => () => loadCustomers());
    return () => {
      // Cleanup khi unmount
      setReloadCallback(null);
    };
  }, [setReloadCallback, sortField, sortDirection, searchTerm]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const customersData = await api.getCustomers();
      const sorted = [...customersData].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        if (sortDirection === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      });
      setCustomers(sorted);
      filterCustomers(sorted, searchTerm);
    } catch (err) {
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = (list: Customer[], term: string) => {
    const filtered = list.filter((c) =>
      c.name.toLowerCase().includes(term.toLowerCase()) ||
      c.phone.includes(term) ||
      (c.cccd && c.cccd.includes(term))
    );
    setFilteredCustomers(filtered);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    filterCustomers(customers, term);
  };

  const handleSort = (field: 'name' | 'phone') => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }

    const sorted = [...customers].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];

      const newDirection = sortField === field ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
      if (newDirection === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });

    setCustomers(sorted);
    filterCustomers(sorted, searchTerm);
  };

  const getSortIndicator = (field: 'name' | 'phone') => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const handleDeleteClick = (customer: Customer) => {
    setSelectedCustomerDelete(customer);
    setDeleteDialogOpen(true);
    setDeleteError('');
  };

  const handleConfirmDelete = async () => {
    if (!selectedCustomerDelete) return;

    setDeleting(true);
    setDeleteError('');
    try {
      await api.deleteCustomer(selectedCustomerDelete.id);
      setCustomers((prev) => prev.filter((c) => c.id !== selectedCustomerDelete.id));
      setFilteredCustomers((prev) => prev.filter((c) => c.id !== selectedCustomerDelete.id));
      setDeleteDialogOpen(false);
      setSelectedCustomerDelete(null);
    } catch (err: any) {
      setDeleteError(err.message || 'Lỗi xóa khách hàng');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedCustomerDelete(null);
    setDeleteError('');
  };
  const handleAddCustomer = () => {
    onEditCustomer({
      id: '',
      name: '',
      phone: '',
      address: '',
      cccd: '',
      customerType: 'Retail',
      createdAt: '',
    });
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Tìm kiếm theo tên, SĐT hoặc CCCD..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />        
        <Tooltip title="Thêm khách hàng mới">
          <Button
            variant="contained"
            onClick={handleAddCustomer}
            disabled={loading}
            color="primary"
          >
            <AddIcon />
          </Button>
        </Tooltip>     
        <Tooltip title="Tải lại">
          <Button
            variant="contained"
            onClick={loadCustomers}
            disabled={loading}
            color="primary"
          >
            <RefreshIcon />
          </Button>
        </Tooltip>   
        </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : filteredCustomers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <Typography variant="body1">
            {searchTerm ? 'Không tìm thấy khách hàng phù hợp' : 'Chưa có khách hàng nào'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>
                  <Button
                    variant="text"
                    onClick={() => handleSort('name')}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 'bold' }}
                  >
                    Tên khách hàng{getSortIndicator('name')}
                  </Button>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>
                  <Button
                    variant="text"
                    onClick={() => handleSort('phone')}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 'bold' }}
                  >
                    Số điện thoại{getSortIndicator('phone')}
                  </Button>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Địa chỉ</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>CCCD</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '10%', textAlign: 'center' }}>Loại KH</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '13%', textAlign: 'center' }}>Hành động</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.map((customer) => (
                <TableRow key={customer.id} hover>
                  <TableCell>{customer.name}</TableCell>
                  <TableCell>{customer.phone}</TableCell>
                  <TableCell sx={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {customer.address}
                  </TableCell>
                  <TableCell>{customer.cccd || '-'}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Chip
                      label={customer.customerType === 'Wholesale' ? '👨🏻‍💼 Thương lái' : '🙍‍♂️ Khách lẻ'}
                      size="small"
                      color={customer.customerType === 'Wholesale' ? 'info' : 'primary'}
                      sx={{ minWidth: '50px', fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title="Tạo phiếu mua">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => onCreatePurchase(customer)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Chỉnh sửa">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onEditCustomer(customer)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isAdmin && (
                      <Tooltip title="Xóa">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(customer)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="caption">
          Tổng cộng: {filteredCustomers.length} / {customers.length} khách hàng
        </Typography>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
          ⚠️ Xác nhận xóa khách hàng
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography>
            Bạn có chắc chắn muốn xóa khách hàng <strong>{selectedCustomerDelete?.name}</strong> ({selectedCustomerDelete?.phone})?
          </Typography>
          <Typography variant="caption" color="error">
            ⚠️ Hành động này không thể được hoàn tác.
          </Typography>
          {deleteError && (
            <Alert severity="error">{deleteError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            Hủy
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {deleting ? 'Đang xóa...' : 'Xóa'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
